import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RichTextEditor } from '../components/RichTextEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Car, Image, MessageSquare, Plus, Pencil, Trash2, LogOut, Eye, Flower2, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { token, isAdmin, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ cars: 0, impressions: 0, contacts: 0, unread_contacts: 0, horses: 0 });
  const [cars, setCars] = useState([]);
  const [impressions, setImpressions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [horses, setHorses] = useState([]);
  const [legalPages, setLegalPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({ title: '', content: '' });
  const [socialLinks, setSocialLinks] = useState([]);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [editingSocial, setEditingSocial] = useState(null);
  const [socialForm, setSocialForm] = useState({ platform: '', url: '', icon: '' });
  const [activeTab, setActiveTab] = useState('cars');
  const [selectedContact, setSelectedContact] = useState(null);

  // Car form
  const [carDialogOpen, setCarDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carForm, setCarForm] = useState({
    stock_number: '', make: '', model: '', year: 2024, price: 0,
    mileage: 0, condition: 'New', images: '', description: '',
    engine: '', transmission: '', fuel_type: '', color: '', featured: false
  });

  // Impression form
  const [impDialogOpen, setImpDialogOpen] = useState(false);
  const [editingImp, setEditingImp] = useState(null);
  const [impForm, setImpForm] = useState({
    title: '', description: '', images: '', video_url: '', category: 'export'
  });

  // Horse form
  const [horseDialogOpen, setHorseDialogOpen] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [horseForm, setHorseForm] = useState({
    name: '', breed: '', age: 0, price: 0, gender: '', color: '',
    description: '', images: '', category: 'horse', featured: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchAll();
    }
  }, [isAdmin]);

  const fetchAll = async () => {
    try {
      const [statsRes, carsRes, impRes, contactsRes, horsesRes, pagesRes, socialRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/cars?limit=200`, { headers }),
        axios.get(`${API}/impressions`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/horses?limit=200`, { headers }),
        axios.get(`${API}/pages`, { headers }),
        axios.get(`${API}/social-links`, { headers }),
      ]);
      setStats(statsRes.data);
      setCars(carsRes.data.cars || []);
      setImpressions(impRes.data.impressions || []);
      setContacts(contactsRes.data.contacts || []);
      setHorses(horsesRes.data.horses || []);
      setLegalPages(pagesRes.data.pages || []);
      setSocialLinks(socialRes.data.links || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  };

  // Car CRUD
  const openAddCar = () => {
    setEditingCar(null);
    setCarForm({
      stock_number: '', make: '', model: '', year: 2024, price: 0,
      mileage: 0, condition: 'New', images: '', description: '',
      engine: '', transmission: '', fuel_type: '', color: '', featured: false
    });
    setCarDialogOpen(true);
  };

  const openEditCar = (car) => {
    setEditingCar(car);
    setCarForm({
      ...car,
      images: (car.images || []).join('\n'),
    });
    setCarDialogOpen(true);
  };

  const saveCar = async () => {
    const payload = {
      ...carForm,
      images: carForm.images.split('\n').map((s) => s.trim()).filter(Boolean),
      year: parseInt(carForm.year),
      price: parseFloat(carForm.price),
      mileage: parseInt(carForm.mileage),
    };
    try {
      if (editingCar) {
        await axios.put(`${API}/cars/${editingCar.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/cars`, payload, { headers });
      }
      setCarDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error('Save car error:', err);
    }
  };

  const deleteCar = async (id) => {
    if (!window.confirm(t('admin.confirm'))) return;
    try {
      await axios.delete(`${API}/cars/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error('Delete car error:', err);
    }
  };

  // Impression CRUD
  const openAddImp = () => {
    setEditingImp(null);
    setImpForm({ title: '', description: '', images: '', video_url: '', category: 'export' });
    setImpDialogOpen(true);
  };

  const openEditImp = (imp) => {
    setEditingImp(imp);
    setImpForm({
      ...imp,
      images: (imp.images || []).join('\n'),
      video_url: imp.video_url || '',
    });
    setImpDialogOpen(true);
  };

  const saveImp = async () => {
    const payload = {
      ...impForm,
      images: impForm.images.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingImp) {
        await axios.put(`${API}/impressions/${editingImp.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/impressions`, payload, { headers });
      }
      setImpDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error('Save impression error:', err);
    }
  };

  const deleteImp = async (id) => {
    if (!window.confirm(t('admin.confirm'))) return;
    try {
      await axios.delete(`${API}/impressions/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error('Delete impression error:', err);
    }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error('Delete contact error:', err);
    }
  };

  // Horse CRUD
  const openAddHorse = () => {
    setEditingHorse(null);
    setHorseForm({
      name: '', breed: '', age: 0, price: 0, gender: '', color: '',
      description: '', images: '', category: 'horse', featured: false
    });
    setHorseDialogOpen(true);
  };

  const openEditHorse = (horse) => {
    setEditingHorse(horse);
    setHorseForm({
      ...horse,
      images: (horse.images || []).join('\n'),
    });
    setHorseDialogOpen(true);
  };

  const saveHorse = async () => {
    const payload = {
      ...horseForm,
      images: horseForm.images.split('\n').map((s) => s.trim()).filter(Boolean),
      age: parseInt(horseForm.age),
      price: parseFloat(horseForm.price),
    };
    try {
      if (editingHorse) {
        await axios.put(`${API}/horses/${editingHorse.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/horses`, payload, { headers });
      }
      setHorseDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error('Save horse error:', err);
    }
  };

  const deleteHorse = async (id) => {
    if (!window.confirm(t('admin.confirm'))) return;
    try {
      await axios.delete(`${API}/horses/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error('Delete horse error:', err);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);

  const openEditPage = (page) => {
    setEditingPage(page);
    setPageForm({ title: page.title, content: page.content });
  };

  const savePage = async () => {
    try {
      await axios.put(`${API}/pages/${editingPage.slug}`, pageForm, { headers });
      setEditingPage(null);
      fetchAll();
    } catch (err) {
      console.error('Save page error:', err);
    }
  };

  // Social link CRUD
  const openAddSocial = () => {
    setEditingSocial(null);
    setSocialForm({ platform: '', url: '', icon: '' });
    setSocialDialogOpen(true);
  };

  const openEditSocial = (link) => {
    setEditingSocial(link);
    setSocialForm({ platform: link.platform, url: link.url, icon: link.icon || '' });
    setSocialDialogOpen(true);
  };

  const saveSocial = async () => {
    try {
      if (editingSocial) {
        await axios.put(`${API}/social-links/${editingSocial.id}`, socialForm, { headers });
      } else {
        await axios.post(`${API}/social-links`, socialForm, { headers });
      }
      setSocialDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error('Save social link error:', err);
    }
  };

  const deleteSocial = async (id) => {
    if (!window.confirm(t('admin.confirm'))) return;
    try {
      await axios.delete(`${API}/social-links/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error('Delete social link error:', err);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#ED2227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-28 pb-24" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white font-[Chivo] uppercase tracking-tight">
              {t('admin.dashboard')}
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => { logout(); navigate('/'); }}
            className="text-zinc-500 hover:text-[#ED2227] text-xs uppercase tracking-widest gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {t('nav.logout')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[
            { label: t('admin.totalCars'), value: stats.cars, icon: Car },
            { label: 'Horses', value: stats.horses, icon: Flower2 },
            { label: t('admin.totalImpressions'), value: stats.impressions, icon: Image },
            { label: t('admin.totalContacts'), value: stats.contacts, icon: MessageSquare },
            { label: t('admin.unreadContacts'), value: stats.unread_contacts, icon: MessageSquare },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-[#121212] border border-zinc-800/50 rounded-md p-5"
              data-testid={`stat-card-${i}`}
            >
              <stat.icon className="w-5 h-5 text-[#ED2227] mb-3" strokeWidth={1.5} />
              <div className="text-2xl font-black text-white font-[Chivo]">{stat.value}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121212] border border-zinc-800/50 rounded-sm h-10 mb-8">
            <TabsTrigger
              value="cars"
              className="text-xs uppercase tracking-widest data-[state=active]:bg-[#ED2227] data-[state=active]:text-white rounded-sm"
              data-testid="tab-cars"
            >
              {t('admin.manageCars')}
            </TabsTrigger>
            <TabsTrigger
              value="impressions"
              className="text-xs uppercase tracking-widest data-[state=active]:bg-[#ED2227] data-[state=active]:text-white rounded-sm"
              data-testid="tab-impressions"
            >
              {t('admin.manageImpressions')}
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="text-xs uppercase tracking-widest data-[state=active]:bg-[#ED2227] data-[state=active]:text-white rounded-sm"
              data-testid="tab-messages"
            >
              {t('admin.viewMessages')}
              {stats.unread_contacts > 0 && (
                <Badge className="ml-2 bg-[#ED2227] text-white text-[10px] px-1.5 border-0">
                  {stats.unread_contacts}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="horses"
              className="text-xs uppercase tracking-widest data-[state=active]:bg-[#ED2227] data-[state=active]:text-white rounded-sm"
              data-testid="tab-horses"
            >
              Horses
            </TabsTrigger>
            <TabsTrigger
              value="pages"
              className="text-xs uppercase tracking-widest data-[state=active]:bg-[#ED2227] data-[state=active]:text-white rounded-sm"
              data-testid="tab-pages"
            >
              Pages
            </TabsTrigger>
          </TabsList>

          {/* Cars Tab */}
          <TabsContent value="cars">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openAddCar}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs gap-2 h-10"
                data-testid="add-car-button"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                {t('admin.addCar')}
              </Button>
            </div>

            <div className="bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800/50 hover:bg-transparent">
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t('inventory.stockNumber')}</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">Vehicle</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t('inventory.year')}</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t('inventory.price')}</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t('inventory.condition')}</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cars.map((car) => (
                    <TableRow key={car.id} className="border-zinc-800/30 hover:bg-white/[0.02]">
                      <TableCell className="font-mono text-xs text-zinc-400">{car.stock_number}</TableCell>
                      <TableCell className="text-white text-sm font-medium">
                        {car.make} {car.model}
                        {car.featured && (
                          <Badge className="ml-2 bg-[#ED2227]/10 text-[#ED2227] text-[10px] border-0">{t('inventory.featured')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">{car.year}</TableCell>
                      <TableCell className="text-[#ED2227] font-bold text-sm">{formatPrice(car.price)}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] border-0 ${car.condition === 'New' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                          {car.condition}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditCar(car)}
                            className="text-zinc-500 hover:text-white h-8 w-8 p-0"
                            data-testid={`edit-car-${car.stock_number}`}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCar(car.id)}
                            className="text-zinc-500 hover:text-[#ED2227] h-8 w-8 p-0"
                            data-testid={`delete-car-${car.stock_number}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {cars.length === 0 && (
                <div className="text-center py-12 text-zinc-600 text-sm">{t('admin.noData')}</div>
              )}
            </div>
          </TabsContent>

          {/* Impressions Tab */}
          <TabsContent value="impressions">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openAddImp}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs gap-2 h-10"
                data-testid="add-impression-button"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                {t('admin.addImpression')}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {impressions.map((imp) => (
                <div
                  key={imp.id}
                  className="bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden"
                  data-testid={`admin-impression-${imp.id}`}
                >
                  {imp.images?.[0] && (
                    <img src={imp.images[0]} alt={imp.title} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-4">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#ED2227]">{imp.category}</span>
                    <h3 className="text-white font-bold mt-1 text-sm">{imp.title}</h3>
                    <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{imp.description}</p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditImp(imp)}
                        className="text-zinc-500 hover:text-white h-8 text-xs"
                        data-testid={`edit-imp-${imp.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteImp(imp.id)}
                        className="text-zinc-500 hover:text-[#ED2227] h-8 text-xs"
                        data-testid={`delete-imp-${imp.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        {t('admin.deleteCar')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {impressions.length === 0 && (
                <div className="col-span-full text-center py-12 text-zinc-600 text-sm">{t('admin.noData')}</div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            {selectedContact ? (
              /* Full message detail view */
              <div className="bg-[#121212] border border-zinc-800/50 rounded-md" data-testid="message-detail-view">
                <div className="p-6 border-b border-zinc-800/50 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedContact(null)}
                    className="text-zinc-400 hover:text-white text-xs uppercase tracking-widest gap-2"
                    data-testid="back-to-messages-btn"
                  >
                    Back to Messages
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { deleteContact(selectedContact.id); setSelectedContact(null); }}
                    className="text-zinc-500 hover:text-[#ED2227] text-xs uppercase tracking-widest gap-1"
                    data-testid="delete-message-detail-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {t('admin.deleteCar')}
                  </Button>
                </div>
                <div className="p-6 md:p-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-1">Name</span>
                      <span className="text-white text-base">{selectedContact.name}</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-1">Email</span>
                      <a href={`mailto:${selectedContact.email}`} className="text-[#ED2227] text-base hover:underline">{selectedContact.email}</a>
                    </div>
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-1">Phone</span>
                      <span className="text-white text-base">{selectedContact.phone || '-'}</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-1">Date</span>
                      <span className="text-zinc-400 text-base">{selectedContact.created_at ? new Date(selectedContact.created_at).toLocaleString() : '-'}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-2">Subject</span>
                    <span className="text-white text-lg font-bold font-[Chivo]">{selectedContact.subject}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-2">Message</span>
                    <div className="bg-zinc-900/50 border border-zinc-800/30 rounded-sm p-5">
                      <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Messages list */
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    className="bg-[#121212] border border-zinc-800/50 rounded-md p-5 hover:border-zinc-700/50 transition-colors cursor-pointer group"
                    onClick={() => setSelectedContact(c)}
                    data-testid={`message-row-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-medium text-sm">{c.name}</span>
                          <span className="text-zinc-600 text-xs">{c.email}</span>
                          {c.phone && <span className="text-zinc-600 text-xs">{c.phone}</span>}
                          <span className="text-zinc-700 text-xs ml-auto shrink-0">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <p className="text-zinc-300 text-sm font-medium truncate">{c.subject}</p>
                        <p className="text-zinc-500 text-xs mt-1 truncate">{c.message}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-zinc-600 group-hover:text-white h-8 w-8 p-0"
                          data-testid={`view-contact-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedContact(c); }}
                        >
                          <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteContact(c.id); }}
                          className="text-zinc-600 hover:text-[#ED2227] h-8 w-8 p-0"
                          data-testid={`delete-contact-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <div className="bg-[#121212] border border-zinc-800/50 rounded-md text-center py-12 text-zinc-600 text-sm">{t('admin.noData')}</div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Horses Tab */}
          <TabsContent value="horses">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openAddHorse}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs gap-2 h-10"
                data-testid="add-horse-button"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Add Horse / Equipment
              </Button>
            </div>

            <div className="bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800/50 hover:bg-transparent">
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">Name</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">Breed</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">Category</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500">Price</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-widest text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {horses.map((horse) => (
                    <TableRow key={horse.id} className="border-zinc-800/30 hover:bg-white/[0.02]">
                      <TableCell className="text-white text-sm font-medium">
                        {horse.name}
                        {horse.featured && (
                          <Badge className="ml-2 bg-[#ED2227]/10 text-[#ED2227] text-[10px] border-0">Featured</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">{horse.breed !== 'N/A' ? horse.breed : '-'}</TableCell>
                      <TableCell>
                        <Badge className="text-[10px] border-0 bg-zinc-800 text-zinc-300">{horse.category}</Badge>
                      </TableCell>
                      <TableCell className="text-[#ED2227] font-bold text-sm">{formatPrice(horse.price)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditHorse(horse)}
                            className="text-zinc-500 hover:text-white h-8 w-8 p-0"
                            data-testid={`edit-horse-${horse.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteHorse(horse.id)}
                            className="text-zinc-500 hover:text-[#ED2227] h-8 w-8 p-0"
                            data-testid={`delete-horse-${horse.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {horses.length === 0 && (
                <div className="text-center py-12 text-zinc-600 text-sm">{t('admin.noData')}</div>
              )}
            </div>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            {editingPage ? (
              <div className="bg-[#121212] border border-zinc-800/50 rounded-md" data-testid="page-editor">
                <div className="p-6 border-b border-zinc-800/50 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white font-[Chivo]">
                    Editing: {editingPage.title}
                  </h3>
                  <div className="flex gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setEditingPage(null)}
                      className="text-zinc-500 text-xs uppercase tracking-widest"
                      data-testid="page-editor-cancel"
                    >
                      {t('admin.cancel')}
                    </Button>
                    <Button
                      onClick={savePage}
                      className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                      data-testid="page-editor-save"
                    >
                      {t('admin.save')}
                    </Button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Page Title</Label>
                    <Input
                      value={pageForm.title}
                      onChange={(e) => setPageForm((p) => ({ ...p, title: e.target.value }))}
                      className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                      data-testid="page-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">
                      Content (Markdown: ## Heading, ### Subheading, **bold**, - list item)
                    </Label>
                    <Textarea
                      value={pageForm.content}
                      onChange={(e) => setPageForm((p) => ({ ...p, content: e.target.value }))}
                      className="bg-zinc-900 border-zinc-800 text-white rounded-sm resize-none font-mono text-xs leading-relaxed"
                      rows={20}
                      data-testid="page-content-input"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs font-mono tracking-widest text-zinc-600 uppercase mb-4">
                  Click "Edit" to modify page content. Changes are reflected immediately on the website.
                </p>
                {legalPages.map((page) => (
                  <div
                    key={page.slug}
                    className="bg-[#121212] border border-zinc-800/50 rounded-md p-6 flex items-center justify-between"
                    data-testid={`page-row-${page.slug}`}
                  >
                    <div className="flex items-center gap-4">
                      <FileText className="w-5 h-5 text-[#ED2227]" strokeWidth={1.5} />
                      <div>
                        <h3 className="text-white font-bold text-sm">{page.title}</h3>
                        <span className="text-zinc-600 text-xs font-mono">/legal/{page.slug}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => openEditPage(page)}
                      className="bg-white/5 text-zinc-300 hover:bg-white/10 rounded-sm text-xs uppercase tracking-widest gap-2 h-9"
                      data-testid={`edit-page-${page.slug}`}
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Edit
                    </Button>
                  </div>
                ))}
                {legalPages.length === 0 && (
                  <div className="text-center py-12 text-zinc-600 text-sm">{t('admin.noData')}</div>
                )}

                {/* Social Media Links */}
                <div className="mt-12 pt-8 border-t border-zinc-800/50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-white font-[Chivo] uppercase tracking-tight">
                      Social Media Links
                    </h3>
                    <Button
                      onClick={openAddSocial}
                      className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs gap-2 h-9"
                      data-testid="add-social-button"
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Add Link
                    </Button>
                  </div>
                  <p className="text-xs font-mono tracking-widest text-zinc-600 uppercase mb-4">
                    Icon options: instagram, facebook, linkedin, youtube, tiktok, twitter, pinterest, snapchat
                  </p>
                  <div className="space-y-3">
                    {socialLinks.map((link) => (
                      <div
                        key={link.id}
                        className="bg-[#121212] border border-zinc-800/50 rounded-md p-4 flex items-center justify-between"
                        data-testid={`social-row-${link.id}`}
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-8 h-8 bg-white/5 border border-zinc-800 rounded-sm flex items-center justify-center text-zinc-400 shrink-0">
                            <span className="text-[10px] font-bold uppercase">{link.icon?.charAt(0) || link.platform?.charAt(0)}</span>
                          </div>
                          <div className="min-w-0">
                            <span className="text-white text-sm font-medium block">{link.platform}</span>
                            <span className="text-zinc-600 text-xs font-mono truncate block">{link.url}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditSocial(link)}
                            className="text-zinc-500 hover:text-white h-8 w-8 p-0"
                            data-testid={`edit-social-${link.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSocial(link.id)}
                            className="text-zinc-500 hover:text-[#ED2227] h-8 w-8 p-0"
                            data-testid={`delete-social-${link.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Car Dialog */}
      <Dialog open={carDialogOpen} onOpenChange={setCarDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-[Chivo] uppercase tracking-tight">
              {editingCar ? t('admin.editCar') : t('admin.addCar')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('inventory.stockNumber')}</Label>
                <Input
                  value={carForm.stock_number}
                  onChange={(e) => setCarForm((p) => ({ ...p, stock_number: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-stock"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Make</Label>
                <Input
                  value={carForm.make}
                  onChange={(e) => setCarForm((p) => ({ ...p, make: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-make"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Model</Label>
                <Input
                  value={carForm.model}
                  onChange={(e) => setCarForm((p) => ({ ...p, model: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-model"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('inventory.year')}</Label>
                <Input
                  type="number"
                  value={carForm.year}
                  onChange={(e) => setCarForm((p) => ({ ...p, year: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-year"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('inventory.price')}</Label>
                <Input
                  type="number"
                  value={carForm.price}
                  onChange={(e) => setCarForm((p) => ({ ...p, price: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('inventory.mileage')}</Label>
                <Input
                  type="number"
                  value={carForm.mileage}
                  onChange={(e) => setCarForm((p) => ({ ...p, mileage: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-mileage"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('inventory.condition')}</Label>
                <Select value={carForm.condition} onValueChange={(v) => setCarForm((p) => ({ ...p, condition: v }))}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm" data-testid="car-form-condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="New" className="text-white">New</SelectItem>
                    <SelectItem value="Used" className="text-white">Used</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('carDetail.engine')}</Label>
                <Input
                  value={carForm.engine}
                  onChange={(e) => setCarForm((p) => ({ ...p, engine: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-engine"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('carDetail.transmission')}</Label>
                <Input
                  value={carForm.transmission}
                  onChange={(e) => setCarForm((p) => ({ ...p, transmission: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-transmission"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('carDetail.fuelType')}</Label>
                <Input
                  value={carForm.fuel_type}
                  onChange={(e) => setCarForm((p) => ({ ...p, fuel_type: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-fuel"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('carDetail.color')}</Label>
                <Input
                  value={carForm.color}
                  onChange={(e) => setCarForm((p) => ({ ...p, color: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="car-form-color"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">{t('carDetail.description')}</Label>
              <RichTextEditor
                value={carForm.description}
                onChange={(html) => setCarForm((p) => ({ ...p, description: html }))}
                placeholder="Add details, key features, options... Use the toolbar for bold and bullet lists."
                testId="car-form-description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Image URLs (one per line)</Label>
              <Textarea
                value={carForm.images}
                onChange={(e) => setCarForm((p) => ({ ...p, images: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white rounded-sm resize-none font-mono text-xs"
                rows={3}
                placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                data-testid="car-form-images"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={carForm.featured}
                onChange={(e) => setCarForm((p) => ({ ...p, featured: e.target.checked }))}
                className="accent-[#ED2227]"
                data-testid="car-form-featured"
                id="featured-check"
              />
              <Label htmlFor="featured-check" className="text-xs uppercase tracking-widest text-zinc-400 cursor-pointer">
                {t('inventory.featured')}
              </Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800/50">
              <Button
                variant="ghost"
                onClick={() => setCarDialogOpen(false)}
                className="text-zinc-500 text-xs uppercase tracking-widest"
                data-testid="car-form-cancel"
              >
                {t('admin.cancel')}
              </Button>
              <Button
                onClick={saveCar}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="car-form-save"
              >
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Impression Dialog */}
      <Dialog open={impDialogOpen} onOpenChange={setImpDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-[Chivo] uppercase tracking-tight">
              {editingImp ? 'Edit Impression' : t('admin.addImpression')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
              <Input
                value={impForm.title}
                onChange={(e) => setImpForm((p) => ({ ...p, title: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                data-testid="imp-form-title"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
              <Textarea
                value={impForm.description}
                onChange={(e) => setImpForm((p) => ({ ...p, description: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white rounded-sm resize-none"
                rows={3}
                data-testid="imp-form-description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Category</Label>
              <Select value={impForm.category} onValueChange={(v) => setImpForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm" data-testid="imp-form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="export" className="text-white">Export</SelectItem>
                  <SelectItem value="preparation" className="text-white">Preparation</SelectItem>
                  <SelectItem value="delivery" className="text-white">Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Image URLs (one per line)</Label>
              <Textarea
                value={impForm.images}
                onChange={(e) => setImpForm((p) => ({ ...p, images: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white rounded-sm resize-none font-mono text-xs"
                rows={3}
                data-testid="imp-form-images"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">YouTube Video URL (optional)</Label>
              <Input
                value={impForm.video_url}
                onChange={(e) => setImpForm((p) => ({ ...p, video_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm font-mono text-xs"
                data-testid="imp-form-video-url"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800/50">
              <Button
                variant="ghost"
                onClick={() => setImpDialogOpen(false)}
                className="text-zinc-500 text-xs uppercase tracking-widest"
                data-testid="imp-form-cancel"
              >
                {t('admin.cancel')}
              </Button>
              <Button
                onClick={saveImp}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="imp-form-save"
              >
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Horse Dialog */}
      <Dialog open={horseDialogOpen} onOpenChange={setHorseDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-[Chivo] uppercase tracking-tight">
              {editingHorse ? 'Edit Horse / Equipment' : 'Add Horse / Equipment'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
                <Input
                  value={horseForm.name}
                  onChange={(e) => setHorseForm((p) => ({ ...p, name: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="horse-form-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Breed</Label>
                <Input
                  value={horseForm.breed}
                  onChange={(e) => setHorseForm((p) => ({ ...p, breed: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="horse-form-breed"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Age (years)</Label>
                <Input
                  type="number"
                  value={horseForm.age}
                  onChange={(e) => setHorseForm((p) => ({ ...p, age: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="horse-form-age"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Price</Label>
                <Input
                  type="number"
                  value={horseForm.price}
                  onChange={(e) => setHorseForm((p) => ({ ...p, price: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="horse-form-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Category</Label>
                <Select value={horseForm.category} onValueChange={(v) => setHorseForm((p) => ({ ...p, category: v }))}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm" data-testid="horse-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="horse" className="text-white">Horse</SelectItem>
                    <SelectItem value="trailer" className="text-white">Trailer</SelectItem>
                    <SelectItem value="equipment" className="text-white">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Gender</Label>
                <Select value={horseForm.gender} onValueChange={(v) => setHorseForm((p) => ({ ...p, gender: v }))}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm" data-testid="horse-form-gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="Stallion" className="text-white">Stallion</SelectItem>
                    <SelectItem value="Mare" className="text-white">Mare</SelectItem>
                    <SelectItem value="Gelding" className="text-white">Gelding</SelectItem>
                    <SelectItem value="N/A" className="text-white">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Color</Label>
                <Input
                  value={horseForm.color}
                  onChange={(e) => setHorseForm((p) => ({ ...p, color: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                  data-testid="horse-form-color"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
              <RichTextEditor
                value={horseForm.description}
                onChange={(html) => setHorseForm((p) => ({ ...p, description: html }))}
                placeholder="Add details, features, training, history... Use the toolbar for bold and bullet lists."
                testId="horse-form-description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Image URLs (one per line)</Label>
              <Textarea
                value={horseForm.images}
                onChange={(e) => setHorseForm((p) => ({ ...p, images: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white rounded-sm resize-none font-mono text-xs"
                rows={3}
                data-testid="horse-form-images"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={horseForm.featured}
                onChange={(e) => setHorseForm((p) => ({ ...p, featured: e.target.checked }))}
                className="accent-[#ED2227]"
                data-testid="horse-form-featured"
                id="horse-featured-check"
              />
              <Label htmlFor="horse-featured-check" className="text-xs uppercase tracking-widest text-zinc-400 cursor-pointer">
                Featured
              </Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800/50">
              <Button
                variant="ghost"
                onClick={() => setHorseDialogOpen(false)}
                className="text-zinc-500 text-xs uppercase tracking-widest"
                data-testid="horse-form-cancel"
              >
                {t('admin.cancel')}
              </Button>
              <Button
                onClick={saveHorse}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="horse-form-save"
              >
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Social Link Dialog */}
      <Dialog open={socialDialogOpen} onOpenChange={setSocialDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-[Chivo] uppercase tracking-tight">
              {editingSocial ? 'Edit Social Link' : 'Add Social Link'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Platform Name</Label>
              <Input
                value={socialForm.platform}
                onChange={(e) => setSocialForm((p) => ({ ...p, platform: e.target.value }))}
                placeholder="e.g. Instagram"
                className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm"
                data-testid="social-form-platform"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">URL</Label>
              <Input
                value={socialForm.url}
                onChange={(e) => setSocialForm((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://instagram.com/yourprofile"
                className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm font-mono text-xs"
                data-testid="social-form-url"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Icon</Label>
              <Select value={socialForm.icon} onValueChange={(v) => setSocialForm((p) => ({ ...p, icon: v }))}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-10 rounded-sm" data-testid="social-form-icon">
                  <SelectValue placeholder="Select icon" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {['instagram', 'facebook', 'linkedin', 'youtube', 'tiktok', 'twitter', 'pinterest', 'snapchat'].map((icon) => (
                    <SelectItem key={icon} value={icon} className="text-white capitalize">{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800/50">
              <Button
                variant="ghost"
                onClick={() => setSocialDialogOpen(false)}
                className="text-zinc-500 text-xs uppercase tracking-widest"
                data-testid="social-form-cancel"
              >
                {t('admin.cancel')}
              </Button>
              <Button
                onClick={saveSocial}
                className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="social-form-save"
              >
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
